import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, downloadFile } from '../api';
import { useToast, useIsAdmin, useRole, StatusBadge, InvoiceBadge, formatDate } from '../components/shared';
import Photo from '../components/Photo';
import ClientForm, { TYPE_BADGE, STATUS, STATUS_BADGE, SPECIALTY, PAYMENT, LOC_SOURCE, MapButton } from '../components/ClientForm';
import { ClientUsers } from './Clients';
import NewTicketModal from '../components/NewTicketModal';
import { NewVisitModal } from './FieldVisits';

function Row({ label, children, ltr }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <div className="text-dim text-sm" style={{ width: 130, flexShrink: 0 }}>{label}</div>
      <div className="text-sm fw-500" dir={ltr ? 'ltr' : undefined} style={{ minWidth: 0, overflowWrap: 'anywhere', textAlign: ltr ? 'right' : undefined }}>{children}</div>
    </div>
  );
}

function Card({ title, children, empty }) {
  return (
    <div className="card" style={{ padding: 18 }}>
      <div className="section-title">{title}</div>
      {children}
      {empty && <div className="text-dim text-sm">{empty}</div>}
    </div>
  );
}

const tel = v => v && <a href={`tel:${v}`}>{v}</a>;
const wa = v => v && <a href={`https://wa.me/${v.replace(/\D/g, '').replace(/^0/, '20')}`} target="_blank" rel="noreferrer">{v}</a>;

export default function ClientDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [edit, setEdit] = useState(false);
  const [logins, setLogins] = useState(false);
  const [newTicket, setNewTicket] = useState(false);
  const [newVisit, setNewVisit] = useState(false);
  const toast = useToast();
  const isAdmin = useIsAdmin();
  const role = useRole();
  const nav = useNavigate();

  const load = useCallback(() => { api.getClient(id).then(setC).catch(e => setErr(e.message)); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="page"><div className="empty">{err}</div></div>;
  if (!c) return <div className="page"><div className="empty">...</div></div>;

  const loc = [c.district, c.area, c.governorate].filter(Boolean).join('، ');
  const hasLoc = c.address || loc || c.landmark || c.lat != null || c.maps_url;
  const hasContact = c.contact_name || c.phone || c.phone2 || c.whatsapp || c.email;

  async function save(form) {
    await api.updateClient(c.id, form);
    toast('اتحفظ'); setEdit(false); load();
  }

  return (
    <div className="page">
      <div className="text-sm mb-16"><Link to="/clients">← Clients</Link></div>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="flex gap-12" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Photo kind="client" id={c.id} version={c.photo_at} size={72} icon="🏥" canEdit={isAdmin || role === 'technician'} canDelete={isAdmin} onChange={load} />
          <div style={{ flex: 1, minWidth: 200 }} dir="rtl">
            <div className="flex gap-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{c.name}</h1>
              <span className="mono text-xs text-dim" dir="ltr">{c.client_code}</span>
            </div>
            <div className="flex gap-8 mt-4" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <span className={`badge ${STATUS_BADGE[c.status] || 'badge-gray'}`}>{STATUS[c.status] || c.status}</span>
              <span className={`badge ${TYPE_BADGE[c.type] || 'badge-gray'}`}>{c.type}</span>
              {c.specialty && <span className="badge badge-blue">{SPECIALTY[c.specialty]}</span>}
              {(c.tags || '').split(',').map(t => t.trim()).filter(Boolean).map(t => <span key={t} className="badge badge-gray">#{t}</span>)}
              {loc && <span className="text-sm text-muted">📍 {loc}</span>}
            </div>
          </div>
          <div className="flex gap-8" style={{ flexWrap: 'wrap' }}>
            <MapButton client={c} />
            <button className="btn btn-ghost" onClick={() => downloadFile(`/api/reports/client/${c.id}`).catch(e => toast(e.message, 'error'))}>⬇ Report</button>
            {isAdmin && <button className="btn btn-ghost" onClick={() => setLogins(true)}>🔑 Logins</button>}
            {(isAdmin || role === 'technician') && <button className="btn btn-primary" onClick={() => setNewVisit(true)}>🚐 زيارة جديدة</button>}
            {isAdmin && <button className="btn btn-ghost" onClick={() => setNewTicket(true)}>🎫 تيكت ورشة</button>}
            {isAdmin && <button className="btn btn-ghost" onClick={() => setEdit(true)}>✎ Edit</button>}
          </div>
        </div>
      </div>

      <div dir="rtl" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 16 }}>
        <Card title="📍 الموقع" empty={!hasLoc && 'مفيش بيانات موقع'}>
          <Row label="العنوان">{c.address}</Row>
          <Row label="المحافظة">{c.governorate}</Row>
          <Row label="المركز / المدينة">{c.area}</Row>
          <Row label="الحي">{c.district}</Row>
          <Row label="علامة مميزة">{c.landmark}</Row>
          <Row label="الإحداثيات" ltr>{c.lat != null && c.lng != null ? `${c.lat}, ${c.lng}` : null}</Row>
          <Row label="مصدر الموقع">{LOC_SOURCE[c.location_source]}</Row>
        </Card>
        <Card title="📞 التواصل" empty={!hasContact && 'مفيش بيانات تواصل'}>
          <Row label="المسئول">{c.contact_name && `${c.contact_name}${c.contact_role ? ` — ${c.contact_role}` : ''}`}</Row>
          <Row label="التليفون" ltr>{tel(c.phone)}</Row>
          <Row label="تليفون تاني" ltr>{tel(c.phone2)}</Row>
          <Row label="واتساب" ltr>{wa(c.whatsapp || c.phone)}</Row>
          <Row label="الإيميل" ltr>{c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}</Row>
        </Card>
        <Card title="💼 العمل">
          <Row label="المهندس المسئول">{c.assigned_engineer}</Row>
          <Row label="طريقة الدفع">{PAYMENT[c.payment_terms]}</Row>
          <Row label="أنسب وقت للزيارة">{c.preferred_visit_time}</Row>
          <Row label="جه منين">{c.source}</Row>
          <Row label="فرع من">{c.parent_name && <Link to={`/clients/${c.parent_client_id}`}>{c.parent_name}</Link>}</Row>
          <Row label="الفروع">{c.branches.length > 0 && c.branches.map((b, i) => <span key={b.id}>{i > 0 && '، '}<Link to={`/clients/${b.id}`}>{b.name}</Link></span>)}</Row>
          <Row label="آخر فاتورة">{c.last_invoice && <span>{c.last_invoice.invoice_number || '—'} · {Number(c.last_invoice.amount || 0).toLocaleString()} ج · <InvoiceBadge status={c.last_invoice.status} /> · <bdi dir="ltr">{formatDate(c.last_invoice.issued_at)}</bdi></span>}</Row>
          <Row label="ملاحظات">{c.notes}</Row>
          <Row label="اتضاف"><bdi dir="ltr">{formatDate(c.created_at)}</bdi></Row>
        </Card>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <div className="section-title">Devices ({c.devices.length}){c.open_tickets > 0 && <span className="badge badge-yellow">{c.open_tickets} open tickets</span>}</div>
        {c.devices.length === 0 ? <div className="text-dim text-sm">No devices yet</div> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {c.devices.map(d => (
              <div key={d.id} onClick={() => nav(`/devices/${d.id}`)} className="flex gap-12" style={{ alignItems: 'center', padding: 10, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}>
                <Photo kind="device" id={d.id} version={d.photo_at} size={44} icon="📟" />
                <div style={{ minWidth: 0 }}>
                  <div className="fw-600 text-sm">{d.name}{d.alias && <span className="text-dim"> · {d.alias}</span>}</div>
                  <div className="mono text-xs text-dim">{d.serial}</div>
                  {d.last_status && <div className="mt-4"><StatusBadge status={d.last_status} /></div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {edit && <ClientForm initial={c} onSave={save} onClose={() => setEdit(false)} />}
      {newVisit && <NewVisitModal clientId={c.id} onClose={() => setNewVisit(false)} />}
      {newTicket && <NewTicketModal clientId={c.id} onClose={() => setNewTicket(false)} />}
      {logins && <ClientUsers client={c} onClose={() => setLogins(false)} />}
    </div>
  );
}
